import { IFluxStandardAction } from "@common";
import { Action } from "redux";

import userFeedback from "./user.feedback";
import { IApiKey, IUser, IDevice } from "@common/models";
import {
  IPaswordLoginRequestData,
  IEditApiKeyRequestData,
  IRemoveDeviceRequestData,
  IResetPasswordReponseData,
  IResetPasswordRequestData,
  IChangePasswordRequestData,
  IChangePasswordResponseData,
  ICreateApiKeyRequestData, IDeleteApiKeyRequestData
} from "./user.models";

export enum UserActions {
  CHANGE_PASSWORD = "@user/password/change",
  CHANGE_PASSWORD_RESPONSE = "@user/password/change/response",
  CREATE_NEW_API_KEY = "@user/apiKey/new",
  CREATE_NEW_API_KEY_RESPONSE = "@user/apiKey/new/response",
  DELETE_API_KEY = "@user/apiKey/delete",
  DELETE_API_KEY_RESPONSE = "@user/apiKey/delete/response",
  DEVICE_NOT_VERIFIED = "@user/device/unknown",
  EDIT_API_KEY = "@user/apiKey/edit",
  GET_API_KEY = "@user/apiKey/get",
  GET_API_KEY_RESPONSE = "@user/apiKey/get/response",
  GET_DEVICES = "@user/device/get",
  GET_DEVICES_RESPONSE = "@user/device/get/response",
  LOGIN_SUCCEEDED = "@user/login/succeeded",
  LOGIN_WITH_PASSWORD = "@user/login",
  LOGOUT = "@user/logout",
  REMOVE_DEVICE = "@user/device/remove",
  RESET_PASSWORD = "@user/password/reset",
  RESET_PASSWORD_RESPONSE = "@user/password/reset/response",
  USER_UPDATE_RESPNOSE = "@user/updated"
}

export interface ICreateNewApiKey extends IFluxStandardAction<ICreateApiKeyRequestData> {
  type: UserActions.CREATE_NEW_API_KEY;
}

export interface IUserUpdateResponse extends IFluxStandardAction<IUser> {
  type: UserActions.USER_UPDATE_RESPNOSE;
}

export interface IEditApiKey
  extends IFluxStandardAction<IEditApiKeyRequestData> {
  type: UserActions.EDIT_API_KEY;
}

export interface IDeleteApiKey extends IFluxStandardAction<IDeleteApiKeyRequestData>{
  type: UserActions.DELETE_API_KEY;
}

export interface IResetPassword
  extends IFluxStandardAction<IResetPasswordRequestData> {
  type: UserActions.RESET_PASSWORD;
}

export interface IResetPasswordReponse
  extends IFluxStandardAction<IResetPasswordReponseData> {
  type: UserActions.RESET_PASSWORD_RESPONSE;
}

export interface ILogout extends IFluxStandardAction<never> {
  type: UserActions.LOGOUT;
}

export interface IGetApiKeys extends Action {
  type: UserActions.GET_API_KEY;
}

export interface IGetApiKeysResponse extends IFluxStandardAction<IApiKey[]> {
  type: UserActions.GET_API_KEY_RESPONSE;
}

export interface ILoginWithPasswordAction
  extends IFluxStandardAction<IPaswordLoginRequestData> {
  type: UserActions.LOGIN_WITH_PASSWORD;
}

export interface IRemoveDevice
  extends IFluxStandardAction<IRemoveDeviceRequestData> {
  type: UserActions.REMOVE_DEVICE;
}

export interface IDeviceNotVerified extends IFluxStandardAction<never> {
  type: UserActions.DEVICE_NOT_VERIFIED;
}

export interface ILoginSuccededAction extends IFluxStandardAction<IUser> {
  type: UserActions.LOGIN_SUCCEEDED;
}

export interface IGetDevices extends IFluxStandardAction<never> {
  type: UserActions.GET_DEVICES;
}

export interface IGetDevicesResponse extends IFluxStandardAction<IDevice[]> {
  type: UserActions.GET_DEVICES_RESPONSE;
}

export interface IChangePasswordRequest
  extends IFluxStandardAction<IChangePasswordRequestData> {
  type: UserActions.CHANGE_PASSWORD;
}

export interface IChangePasswordResponse
  extends IFluxStandardAction<IChangePasswordResponseData> {
  type: UserActions.CHANGE_PASSWORD_RESPONSE;
}

export type UserActionTypes =
  | IChangePasswordResponse
  | IDeviceNotVerified
  | IGetDevicesResponse
  | IGetDevices
  | IGetApiKeysResponse
  | ILoginSuccededAction
  | ILoginWithPasswordAction
  | ILogout
  | IUserUpdateResponse;

export function resetPassword(username: string): IResetPassword {
  return { type: UserActions.RESET_PASSWORD, payload: { username } };
}

export function resetPasswordResponse(
  payload: Error,
  feedback: string = null
): IResetPasswordReponse {
  return {
    type: UserActions.RESET_PASSWORD_RESPONSE,
    payload,
    meta: { feedback }
  };
}

export function userUpdateResponse(
  payload: IUser | Error,
  feedback: string = null
): IUserUpdateResponse {
  return {
    type: UserActions.USER_UPDATE_RESPNOSE,
    payload,
    meta: { feedback }
  };
}

export function deleteApiKey(userId: string, apiKeyName: string): IDeleteApiKey {
  return {
    type: UserActions.DELETE_API_KEY,
    payload: {userId, apiKeyName}
  }
}

export function createNewApiKey(userId: string): ICreateNewApiKey {
  return { type: UserActions.CREATE_NEW_API_KEY, payload: { userId } };
}

export function editApiKey(apiKey: IApiKey, newName: string): IEditApiKey {
  return { type: UserActions.EDIT_API_KEY, payload: { apiKey, newName } };
}

export function logout(): ILogout {
  return { type: UserActions.LOGOUT, payload: null };
}

export function getApiKeys(): IGetApiKeys {
  return { type: UserActions.GET_API_KEY };
}

export function removeDevice(userId: string, deviceId: string): IRemoveDevice {
  return {
    type: UserActions.REMOVE_DEVICE,
    payload: { userId, deviceId }
  };
}

export function getApiKeysResponse(
  payload: IApiKey[] | Error,
  feedback: string = null
): IGetApiKeysResponse {
  return {
    type: UserActions.GET_API_KEY_RESPONSE,
    payload,
    meta: { feedback }
  };
}

export function getDevices(): IGetDevices {
  return {
    type: UserActions.GET_DEVICES,
    payload: null
  };
}

export function getDevicesResponse(
  payload: IDevice[] | Error,
  feedback: string = null
): IGetDevicesResponse {
  return {
    type: UserActions.GET_DEVICES_RESPONSE,
    payload,
    meta: { feedback }
  };
}

export function deviceNotVerified(): IDeviceNotVerified {
  return {
    type: UserActions.DEVICE_NOT_VERIFIED,
    payload: null,
    meta: { feedback: userFeedback.DEVICE_NOT_VERIFIED }
  };
}

export function loginWithPassword(
  username: string,
  password: string
): ILoginWithPasswordAction {
  return {
    type: UserActions.LOGIN_WITH_PASSWORD,
    payload: {
      username,
      password
    }
  };
}

export function loginSucceded(
  payload: IUser | Error,
  feedback: string = null
): ILoginSuccededAction {
  return {
    type: UserActions.LOGIN_SUCCEEDED,
    payload,
    meta: { feedback }
  };
}

export function changePassword(
  oldPassword: string,
  newPassword: string
): IChangePasswordRequest {
  return {
    type: UserActions.CHANGE_PASSWORD,
    payload: { oldPassword, newPassword }
  };
}
export function changePasswordResponse(
  message: string,
  feedback: string = null
): IChangePasswordResponse {
  return {
    type: UserActions.CHANGE_PASSWORD_RESPONSE,
    payload: { message },
    meta: { feedback }
  };
}
